import React, { useCallback, useMemo } from 'react';

import cn from 'classnames';
import { upperCase } from 'lodash';
import capitalize from 'lodash/capitalize';
import PropTypes from 'prop-types';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import {
  deleteSubUnitRequest,
  setSelectedSubUnitId,
  showSetSubUnitModal,
} from '@modules/advancement/subUnits/duck/actions';
import { subUnitTypeSel } from '@modules/advancement/subUnits/duck/selectors';
import { denTypesSingleNames, hasNewPermissionSel } from '@shared';
import { ExpandToggle } from '@shared/components';
import { newPermissions } from '@shared/newPermissions';

import {
  currentUserSubunitsSel,
  setSubUnitsSectionExpanded,
} from '../../../../duck';
import styles from './MainRow.less';
import MainRowButtons from './MainRowButtons';

function MainRow(props) {
  const {
    subUnitId,
    subUnitName,
    subUnitNameRaw,
    denType,
    itemsCount,
    expanded,
    hideExpanded = false,
    //TODO: remove membersLength when SBL-6710 is fixed
    membersLength,
  } = props;
  const dispatch = useDispatch();
  const isSubUnit = !!subUnitName;
  const subUnitTypeName = useSelector(subUnitTypeSel);
  const canEditAllSubUnits = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.EDIT_UNIT),
  );
  const mySubUnits = useSelector(currentUserSubunitsSel);
  const canEditMySubUnits = useSelector(state =>
    hasNewPermissionSel(state, newPermissions.EDIT_SUB_UNIT),
  );
  const hasSubUnitOnlyPerms = useMemo(
    () => canEditMySubUnits,
    [canEditMySubUnits],
  );

  const canDoActions = useMemo(() => {
    if (hasSubUnitOnlyPerms && !canEditAllSubUnits) {
      const hasMatchingSubUnits = mySubUnits.some(sub => {
        const mySubId = sub.denId || sub.patrolId;
        return mySubId === subUnitId;
      });
      return hasMatchingSubUnits;
    }

    return canEditAllSubUnits;
  }, [mySubUnits, hasSubUnitOnlyPerms, subUnitId, canEditAllSubUnits]);

  const denTypeDisplay = useMemo(() => {
    if (!denType) {
      return '';
    }

    const denTypeSingleName = denTypesSingleNames[denType];

    if (denTypeSingleName === denTypesSingleNames.aol)
      return `${upperCase(denTypeSingleName)} `;

    return denTypeSingleName
      ? `${capitalize(denTypeSingleName)} `
      : `${capitalize(denType)} `;
  }, [denType]);

  const handleOnClick = useCallback(() => {
    dispatch(setSubUnitsSectionExpanded(subUnitId, !expanded));
  }, [dispatch, subUnitId, expanded]);

  const handleOnDeleteSubUnit = useCallback(() => {
    dispatch(deleteSubUnitRequest(subUnitId));
  }, [dispatch, subUnitId]);

  const handleOnEditSubUnit = useCallback(() => {
    dispatch(setSelectedSubUnitId(subUnitId));
    dispatch(showSetSubUnitModal());
  }, [dispatch, subUnitId]);

  return (
    <div className={styles.mainRow}>
      <div className={styles.denInfoWrapper}>
        <div>
          {isSubUnit ? (
            <FormattedMessage
              id="packRoster.MainRow.subUnitRowName"
              values={{
                subUnitTypeName: capitalize(subUnitTypeName),
                subUnitName: subUnitNameRaw,
                denType: denTypeDisplay,
              }}
            />
          ) : (
            <FormattedMessage id="packRoster.MainRow.unassigned" />
          )}
          <span className={styles.count}>({itemsCount})</span>
        </div>
        {!hideExpanded && (
          <ExpandToggle
            className={cn(styles.chevron, {
              [styles.noDisplay]: false,
            })}
            expanded={expanded}
            onClick={handleOnClick}
          />
        )}
      </div>
      {isSubUnit && subUnitName && canDoActions && (
        <MainRowButtons
          deleteConfirmationMessage={
            <FormattedHTMLMessage
              id={
                membersLength
                  ? 'packRoster.MainRow.confirmTempEditSubUnit'
                  : 'packRoster.MainRow.confirmDeleteSubUnit'
              }
              values={{
                subUnitName: subUnitName,
                subUnitTypeName: capitalize(subUnitTypeName),
              }}
            />
          }
          onDelete={membersLength ? handleOnEditSubUnit : handleOnDeleteSubUnit}
          onEdit={handleOnEditSubUnit}
        />
      )}
    </div>
  );
}

MainRow.propTypes = {
  subUnitId: PropTypes.number,
  subUnitName: PropTypes.string,
  subUnitNameRaw: PropTypes.string,
  denType: PropTypes.string,
  itemsCount: PropTypes.number.isRequired,
  expanded: PropTypes.bool.isRequired,
  hideExpanded: PropTypes.bool,
  //TODO: remove membersLength when SBL-6710 is fixed
  membersLength: PropTypes.number.isRequired,
};

export default MainRow;
